import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';

export default function ReglementAdhesion() {
  const { adhesionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [adhesion, setAdhesion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Vérifier si on revient d'un paiement réussi
    const success = searchParams.get('success');
    console.log('success param: ', success)
    if (success === 'true') {
      setPaymentSuccess(true);
    }

    fetchAdhesionData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, adhesionId, searchParams]);

  // Charger la clé Stripe appropriée selon l'organisme
  useEffect(() => {
    if (adhesion?.organisme) {
      let stripeKey;
      
      if (adhesion.organisme === 'SAR') {
        stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY_SAR;
      } else if (adhesion.organisme === 'AMAIR') {
        stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY_AMAIR;
      } else if (adhesion.organisme === 'ADAR') {
        stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY_ADAR;
      }
      
      console.log(`🔑 Chargement de la clé Stripe pour ${adhesion.organisme}`);
      console.log(`🔑 Clé utilisée: ${stripeKey ? stripeKey.substring(0, 10) + '...' : 'NON DÉFINIE'}`);
      setStripePromise(loadStripe(stripeKey));
    }
  }, [adhesion?.organisme]);

  const fetchAdhesionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/payment/adhesion/${adhesionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAdhesion(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Vous n\'êtes pas autorisé à accéder à cette page');
      } else if (err.response?.status === 400) {
        setError('Cette adhésion n\'est pas en attente de paiement');
      } else {
        setError('Erreur lors du chargement des informations');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const stripe = await stripePromise;

      // Créer une session de paiement
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/create-payment-session/${adhesionId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Rediriger vers Stripe Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Erreur lors de l\'initialisation du paiement');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !adhesion) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-soft p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Accès refusé</h3>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/mes-adhesions')}
            className="w-full bg-honey-500 text-white py-2 px-4 rounded-lg hover:bg-honey-600 transition-colors"
          >
            Retour aux adhésions
          </button>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-soft p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Paiement réussi !</h3>
          <p className="text-slate-600 mb-6">
            Votre paiement a été effectué avec succès. Vous allez recevoir un email de confirmation.
          </p>
          <button
            onClick={() => navigate('/mes-adhesions')}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
          >
            Retour aux adhésions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {/* En-tête */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-8 text-white">
            <h1 className="text-3xl font-bold mb-2">Règlement de mon adhésion</h1>
            <p className="text-indigo-100">Finalisez votre adhésion en effectuant le paiement</p>
          </div>

          {/* Contenu */}
          <div className="px-6 py-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Informations de l'adhésion */}
            <div className="mb-8">
              <h2 className="text-xl font-semilbold text-slate-900 mb-4">Détails de votre adhésion</h2>
              <div className="bg-slate-50 rounded-lg p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Organisme :</span>
                  <span className="font-semibold text-slate-900">{adhesion?.organisme}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Adhérent :</span>
                  <span className="font-semibold text-slate-900">{adhesion?.userName}</span>
                </div>
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900">Montant à régler :</span>
                    <span className="text-2xl font-bold text-honey-600">
                      {adhesion?.montant?.toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations de paiement */}
            <div className="mb-8">
              <h2 className="text-xl font-semilbold text-slate-900 mb-4">Paiement sécurisé</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Paiement 100% sécurisé</p>
                    <p>Votre paiement est traité de manière sécurisée par Stripe. Vos informations bancaires ne sont jamais stockées sur nos serveurs.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bouton de paiement */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gray-200 text-slate-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handlePayment}
                disabled={processing}
                className="flex-1 bg-honey-500 text-white py-3 px-6 rounded-lg hover:bg-honey-600 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Traitement...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Procéder au paiement
                  </>
                )}
              </button>
            </div>

            {/* Footer informations */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center">
                En cliquant sur "Procéder au paiement", vous serez redirigé vers notre page de paiement sécurisée.
              </p>
            </div>
          </div>
        </div>

        {/* Informations complémentaires */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Besoin d'aide ?</h3>
          <p className="text-slate-600 text-sm mb-2">
            Si vous rencontrez des difficultés lors du paiement, n'hésitez pas à nous contacter.
          </p>
          <p className="text-slate-600 text-sm">
            Email : <a href="mailto:abeillereunion@gmail.com" className="text-honey-600 hover:text-honey-700">abeillereunion@gmail.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}